import React from "react";
import { motion } from "framer-motion";
import { Container } from "./Container";
import { Eyebrow } from "./Eyebrow";
import { Heading } from "./Heading";
import { AnimatedGrid } from "./AnimatedGrid";

const fade = {
    hidden: { opacity: 0, y: 16 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.75,
            delay: i * 0.08,
            ease: [0.16, 1, 0.3, 1],
        },
    }),
};

/**
 * Shared subpage hero — eyebrow / H1 / sub / optional CTAs / animated grid bg.
 * Optionally renders a brand logo image above the eyebrow.
 */
export const PageHero = ({
    eyebrow,
    title,
    subtitle,
    children,
    logoSrc,
    logoAlt,
    logoMaxHeightClass = "max-h-28 sm:max-h-36",
    logoTagline,
    "data-testid": testId,
}) => {
    return (
        <section
            className="relative isolate overflow-hidden pt-[88px]"
            data-testid={testId}
        >
            <AnimatedGrid />
            <Container className="relative z-10">
                <motion.div
                    initial="hidden"
                    animate="show"
                    variants={{
                        show: { transition: { staggerChildren: 0.08 } },
                    }}
                    className="max-w-3xl py-24 lg:py-32"
                >
                    {logoSrc && (
                        <motion.div variants={fade} custom={0} className="mb-8">
                            <img
                                src={logoSrc}
                                alt={logoAlt || ""}
                                loading="lazy"
                                className={`block w-auto ${logoMaxHeightClass}`}
                                style={{
                                    filter:
                                        "drop-shadow(0 0 40px rgba(124,255,79,0.08))",
                                }}
                            />
                        </motion.div>
                    )}
                    {eyebrow && (
                        <motion.div variants={fade} custom={0}>
                            <Eyebrow withDot>{eyebrow}</Eyebrow>
                        </motion.div>
                    )}
                    <motion.div variants={fade} custom={1}>
                        <Heading as="h1" fluidSize="h1" className="mt-6">
                            {title}
                        </Heading>
                    </motion.div>
                    {subtitle && (
                        <motion.p
                            variants={fade}
                            custom={2}
                            className="mt-7 max-w-2xl text-[17px] leading-[1.65] text-text-secondary"
                        >
                            {subtitle}
                        </motion.p>
                    )}
                    {logoTagline && (
                        <motion.p
                            variants={fade}
                            custom={2.5}
                            className="mt-3 text-[13px] text-text-muted"
                        >
                            {logoTagline}
                        </motion.p>
                    )}
                    {children && (
                        <motion.div
                            variants={fade}
                            custom={3}
                            className="mt-10 flex flex-wrap items-center gap-3"
                        >
                            {children}
                        </motion.div>
                    )}
                </motion.div>
            </Container>
        </section>
    );
};

export default PageHero;
